import type { Router } from 'vue-router';

import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { loginRedirect } from '@/utils/redirect';

interface OtpErrorData {
  otp_provisioning_uri?: string;
  otp_invalid?: boolean;
  app_based_otp?: boolean;
  errors?: { otp_code?: unknown };
}

function otpErrorResponse(error: unknown) {
  return (error as { response?: { status?: number; data?: OtpErrorData } } | null)?.response;
}

function isOtpError({ error }: { error: unknown }) {
  const response = otpErrorResponse(error);
  const data = response?.data;
  if (response?.status === 400) {
    if (data?.otp_provisioning_uri) {
      return true;
    }
    if (data?.otp_invalid) {
      return true;
    }
    if (data?.errors?.otp_code) {
      return true;
    }
  } else if (response?.status === 401) {
    if (data?.errors?.otp_code) {
      return true;
    }
    if (data?.otp_invalid) {
      return true;
    }
  }
  return false;
}

export const useOneTimePasswordStore = defineStore('oneTimePassword', () => {
  const showPopup = ref(false);
  const provisioningUri = ref<string | null>(null);
  const appBasedOtp = ref(false);
  const otpRequired = ref(false);
  const userEmail = ref<string | null>(null);
  const userPassword = ref<string | null>(null);
  const setupRequired = computed(() => !!provisioningUri.value);

  function launchPopup({ email, password }: { email: string; password: string }) {
    userEmail.value = email;
    userPassword.value = password;
    showPopup.value = true;
  }

  function closePopup() {
    provisioningUri.value = null;
    appBasedOtp.value = false;
    otpRequired.value = false;
    userEmail.value = null;
    userPassword.value = null;
    showPopup.value = false;
  }

  function onLoginSuccess({ router }: { router?: Router | null }) {
    closePopup();
    if (router) {
      loginRedirect(router);
    }
  }

  function handleLoginError({
    error,
    email,
    password,
  }: {
    error: unknown;
    email?: string;
    password?: string;
  }) {
    if (isOtpError({ error })) {
      const data = otpErrorResponse(error)?.data;

      if (data?.otp_provisioning_uri) {
        provisioningUri.value = data.otp_provisioning_uri;
      }
      if (data?.app_based_otp) {
        appBasedOtp.value = true;
      }
      if (data?.otp_invalid) {
        otpRequired.value = true;
      }

      if (email && password) {
        launchPopup({ email, password });
      }

      return true;
    }
    return false;
  }

  return {
    isOtpError,
    handleLoginError,
    setupRequired,
    provisioningUri,
    appBasedOtp,
    showPopup,
    userEmail,
    userPassword,
    otpRequired,
    closePopup,
    onLoginSuccess,
    launchPopup,
  };
});
